import map from 'lodash-es/map';
import { FILE_IS_TOO_BIG, WRONG_IMAGE_FORMAT } from 'mk/autogenerated/translations/Uploader.5d65b87a60a1c57d70693ad780c1296b'
import picUp from 'mk/common/picUp';
import { Context } from 'mk/interfaces';
import { photo_upload_url } from 'mk/urls/functions';
import { OldPhotoUploadResponse, PhotoAspect, PhotoEntity, PhotoMimetype } from 'mk2/schemas';
import * as React from 'react';

const MAX_UPLOAD_FILE_SIZE = 15 * 1024 * 1024;

export const STATUS = {
    NEW: 1,
    SENDING: 2,
    DONE: 3,
    FAILED: 4,
};

interface OwnProps {
    context: Context;
    photoType: string;
    multiple?: boolean;
    initialPhotos?: any[]; // array of photo tokens
    useNativeAndroidUpload: boolean;
    useGenericFileSelectDialog: boolean;

    onFilesUpdated(files: FileState[]);
}

interface FileState {
    id: number;
    status: number;
    errorMsg: string;
    progress: number;
    obj?: File;
    orientation: number;

    code: string;
    width: number;
    height: number;
    aspect: PhotoAspect;
    mimetype: PhotoMimetype;
}

interface State {
    files: FileState[];
}

export class Uploader extends React.Component<OwnProps, State> {

    public static defaultProps = {
        multiple: true,
        initialPhotos: [],
    };

    private lastId = 0;
    private androidUploadElem: HTMLAnchorElement;
    private fileInputElem: HTMLInputElement;

    constructor(props: OwnProps, context) {
        super(props, context);

        let files = [];
        if (props.initialPhotos) {
            files = props.initialPhotos.map(this.doneState);
        }

        this.state = {
            files,
        };
    }

    public componentWillUnmount() {
        if (this.props.useNativeAndroidUpload && (window as any).mkn_uploadend === this.handleNativeAndroidUploadCallback) {
            (window as any).mkn_uploadend = undefined;
        }
    }

    public render() {
        /* crazy invisible input
        we must support android-webkit-4, but it does this: if you call click() method
        on a display-none file-input, it ignores it. so we have to hide it more creatively
        and this approach seems to work */
        return this.props.useNativeAndroidUpload ? (
            <a
                href={`mkn://upload?multi=${this.props.multiple ? 1 : 0}&url=${photo_upload_url(this.props.photoType)}`}
                ref={this.handleAndroidUploadRef}
            />
        ) : (
            <div className="uploader">
                <input
                    id="invisible"
                    ref={this.handleFileInputRef}
                    type="file"
                    accept={
                        // Chrome 66 na Androide ma bug, ze 'Select Image' obrazovka zamrzne,
                        // vyvolaj intent na vyber lubovolneho suboru, nie len intent na vyber obrazkov
                        this.props.useGenericFileSelectDialog
                            ? undefined
                            : 'image/jpeg,image/png,image/gif'
                    }
                    multiple={this.props.multiple}
                    onChange={this.startUploadingFiles}
                    style={{position: 'absolute', left: -5000, zIndex: -100, width: 0, height: 0}}
                />
            </div>
        );
    }

    public openFileExplorer() {
        if (this.props.useNativeAndroidUpload) {
            (window as any).mkn_uploadend = this.handleNativeAndroidUploadCallback;
            this.androidUploadElem.click();
        } else {
            this.fileInputElem.click();
        }
    }

    // use in componentDidMount with addEventListener('click')
    public cancel(fileId) {
        const files = this.state.files.filter((f) => f.id !== fileId);
        this.setState({ files });
        this.props.onFilesUpdated(files);
    }

    public bulkCancel(fileIds) {
        const files = this.state.files.filter((f) => fileIds.indexOf(f.id) === -1);
        this.setState({ files });
        this.props.onFilesUpdated(files);
    }

    public rotate(fileId) {
        const files = this.state.files.map((f) => {
            if (f.id === fileId) {
                return {
                    ...f,
                    orientation: (f.orientation + 1) % 4,
                };
            } else {
                return f;
            }
        });
        this.setState({ files });
        this.props.onFilesUpdated(files);
    }

    public clear() {
        this.setState({ files: [] });
        this.props.onFilesUpdated([]);
    }

    private getNextId() {
        this.lastId = this.lastId + 1;
        return this.lastId;
    }

    private initialState = (f): FileState => {
        let errorMsg = null;
        if (f.size > MAX_UPLOAD_FILE_SIZE) {
            errorMsg = FILE_IS_TOO_BIG;
        } else if (f.type !== '' && f.type !== 'image/jpeg' && f.type !== 'image/png' && f.type !== 'image/gif') { // if the type cannot be determined, the value is empty-string
            errorMsg = WRONG_IMAGE_FORMAT;
        }
        const status = errorMsg ? STATUS.FAILED : STATUS.NEW;
        return {
            id: this.getNextId(),
            status,
            errorMsg,
            progress: 0,
            obj: f,
            orientation: 0,
            code: null,
            width: 0,
            height: 0,
            mimetype: null,
            aspect: null,
        };
    };

    private doneState = (photo: PhotoEntity) => {
        return {
            id: this.getNextId(),
            status: STATUS.DONE,
            errorMsg: null,
            progress: 1,
            obj: null,
            orientation: 0,
            code: photo.code,
            mimetype: photo.mimetype,
            width: photo.width,
            height: photo.height,
            aspect: photo.aspect,
        };
    };

    private startUploadingFiles = () => {
        if (!this.fileInputElem.files.length) {
            // Ignore empty changes
            return;
        }

        const files = this.props.multiple
            ? [...this.state.files, ...map<File, FileState>(this.fileInputElem.files, this.initialState)]
            : [this.fileInputElem.files[0]].map(this.initialState);

        this.setState({ files }, this.loop);
        this.props.onFilesUpdated(files);
        this.fileInputElem.value = ''; // so that we can select the same file again later
    };

    private setFileState(fileId, data) {
        const files = this.state.files.map((f) => {
            if (f.id === fileId) {
                return {
                    ...f,
                    ...data,
                };
            } else {
                return f;
            }
        });
        this.setState({ files });
        this.props.onFilesUpdated(files);
    }

    // activates the queue
    private loop = () => {
        // first we check if anything is uploading currently
        if (this.state.files.some((f) => f.status === STATUS.SENDING)) {
            return;
        }

        // we pick the first available file
        const newFiles = this.state.files.filter((f) => f.status === STATUS.NEW);

        if (newFiles.length === 0) {
            // console.log('no available files');
            return;
        }

        const nextFile = newFiles[0];

        this.setFileState(nextFile.id, { status: STATUS.SENDING, progress: 0 });

        // adminom nemodifikuj nahraty obrazok, ak sa da. Napr. Vivi to pouziva v nativnych kampaniach
        const isAdmin = this.props.context.requestUserPermissions.indexOf('forum.can_manage_forum') >= 0;
        picUp(nextFile.obj, photo_upload_url(this.props.photoType), !isAdmin,
            (data) => {
                // console.log('loop: done successfully');
                this.setFileState(nextFile.id, {status: STATUS.DONE, ...data.photo});
                this.loop();
            },
            (errorMsg) => {
                // console.log('loop: failed:'+errorMsg);
                this.setFileState(nextFile.id, {status: STATUS.FAILED, errorMsg});
                this.loop();
            },
            (progress) => {
                this.setFileState(nextFile.id, {progress});
                // console.log('loop: progress:'+progress);
            },
        );
    };

    private handleFileInputRef = (el: HTMLInputElement) => {
        this.fileInputElem = el;
    };

    private handleAndroidUploadRef = (el: HTMLAnchorElement) => {
        this.androidUploadElem = el;
    };

    // callback, ktory vola nativna appka spat do stranky ked dokonci upload subor(ov)
    private handleNativeAndroidUploadCallback = (res: OldPhotoUploadResponse) => {
        const f = this.doneState(res.photo);

        const files = this.props.multiple ? [...this.state.files, f] : [ f ];
        this.setState({
            files,
        });

        this.props.onFilesUpdated(files);
    };
}
